/**
 * wavPeaks — WAV(PCM 16비트) 바이트에서 파형 봉우리를 뽑는다. 반주 진행 표시(`BackingWaveform`)의
 * 그림 재료다.
 *
 * **`AudioContext`로 디코딩하지 않는다.** 반주 엘리먼트를 WebAudio 그래프에 물렸을 때 iOS 가
 * 그래프를 재우면서 반주가 통째로 사라진 이력이 있다(`SongReferenceAudio` 머리말). 진단 곡은
 * `pcm_s16le`라 헤더만 걸으면 샘플이 그대로 있다.
 *
 * 못 읽는 입력(PCM 16비트가 아님·청크 누락·잘린 파일)은 던지지 않고 빈 배열이다 — 파형은
 * 장식이라, 없다고 노래 진단이 멈추면 안 된다. 곡이 압축 포맷으로 바뀌면 여기가 빈 배열을
 * 내기 시작하므로 그때는 빌드 타임에 봉우리를 뽑는 쪽으로 옮긴다.
 *
 * 값은 버킷별 |샘플| 최대를 32768 로 나눈 0..1. 버킷 수가 프레임 수보다 많으면 프레임 수만큼만
 * 낸다(빈 버킷을 지어내지 않는다). 남는 프레임은 마지막 버킷이 안는다.
 */

const PCM = 1;

const tag = (view: DataView, at: number): string =>
  String.fromCharCode(
    view.getUint8(at),
    view.getUint8(at + 1),
    view.getUint8(at + 2),
    view.getUint8(at + 3),
  );

export const wavPeaks = (buffer: ArrayBuffer, buckets: number): number[] => {
  const view = new DataView(buffer);
  if (
    buckets <= 0 ||
    view.byteLength < 12 ||
    tag(view, 0) !== 'RIFF' ||
    tag(view, 8) !== 'WAVE'
  ) {
    return [];
  }

  let channels = 0;
  let pcm16 = false;
  // RIFF 청크는 순서가 정해져 있지 않고(`LIST` 가 끼곤 한다) 홀수 길이면 패딩 1바이트가 붙는다.
  for (let at = 12; at + 8 <= view.byteLength; ) {
    const id = tag(view, at);
    const size = view.getUint32(at + 4, true);
    const body = at + 8;
    if (id === 'fmt ' && body + 16 <= view.byteLength) {
      pcm16 =
        view.getUint16(body, true) === PCM &&
        view.getUint16(body + 14, true) === 16;
      channels = view.getUint16(body + 2, true);
    }
    if (id === 'data') {
      if (!pcm16 || channels < 1) {
        return [];
      }
      const end = Math.min(body + size, view.byteLength);
      const frameBytes = 2 * channels;
      const frames = Math.floor((end - body) / frameBytes);
      const count = Math.min(buckets, frames);
      if (count === 0) {
        return [];
      }
      const per = Math.floor(frames / count);
      const peaks = new Array<number>(count).fill(0);
      for (let f = 0; f < frames; f += 1) {
        const bucket = Math.min(count - 1, Math.floor(f / per));
        const base = body + f * frameBytes;
        for (let c = 0; c < channels; c += 1) {
          const v = Math.abs(view.getInt16(base + c * 2, true)) / 32_768;
          if (v > peaks[bucket]) {
            peaks[bucket] = v;
          }
        }
      }
      return peaks;
    }
    at = body + size + (size % 2);
  }
  return [];
};
